/**
 * AZRIA — Card Add Button (v2)
 * ====================================================================
 * Handles the floating + button on product cards (collection grids,
 * Best Sellers, New Arrivals, related products, etc).
 *
 * Two button flavors, decided by Liquid (card-product.liquid):
 *
 *   A) SINGLE-VARIANT product → button has data-variant-id.
 *      Tap + → POST /cart/add.js → + → spinner → ✓ → reverts.
 *      (Unchanged v1 behavior.)
 *
 *   B) ONE-OPTION product (e.g. Size only) → button has
 *      data-has-options="true" and NO data-variant-id. Tap + →
 *      opens sibling .az-card-size-strip with size pills →
 *      pill tap → same AJAX add flow, feedback shown on the + button.
 *      Sold-out pills are disabled (Liquid). Strip closes on:
 *      pill tap, outside tap, Esc, or opening another card's strip.
 *
 *   Multi-axis products (Size + Color): no button rendered (Liquid).
 *
 * Cart feedback: header bubble updates; cart drawer silently
 * refreshes without opening ("silent" feedback mode).
 * ====================================================================
 */
(function () {
  'use strict';

  var SUCCESS_DURATION = 1200; // ms — checkmark stays visible
  var ERROR_DURATION = 1800;   // ms — error state stays visible

  /* ------------------------------------------------------------------
     Strip helpers
     ------------------------------------------------------------------ */

  function getStripFor(btn) {
    // Strip is rendered as a sibling of the button inside .card__media
    var media = btn.closest('.card__media');
    return media ? media.querySelector('.az-card-size-strip') : null;
  }

  function closeAllStrips(except) {
    var open = document.querySelectorAll('.az-card-size-strip.is-open');
    for (var i = 0; i < open.length; i++) {
      if (open[i] !== except) {
        open[i].classList.remove('is-open');
        var media = open[i].closest('.card__media');
        var ownerBtn = media && media.querySelector('.az-card-add-btn');
        if (ownerBtn) ownerBtn.setAttribute('aria-expanded', 'false');
      }
    }
  }

  function toggleStrip(btn) {
    var strip = getStripFor(btn);
    if (!strip) return;
    var willOpen = !strip.classList.contains('is-open');
    closeAllStrips(willOpen ? strip : null);
    strip.classList.toggle('is-open', willOpen);
    btn.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
  }

  /* ------------------------------------------------------------------
     Rect-hit helper — shared by button + pill detection.
     Dawn's card overlay link intercepts DOM click targets, so we
     fall back to screen-coordinate detection (capture phase).
     ------------------------------------------------------------------ */

  function rectHit(el, e) {
    var rect = el.getBoundingClientRect();
    return (
      rect.width > 0 &&
      e.clientX >= rect.left &&
      e.clientX <= rect.right &&
      e.clientY >= rect.top &&
      e.clientY <= rect.bottom
    );
  }

  /* ------------------------------------------------------------------
     CAPTURE-phase click listener — runs BEFORE overlay-link navigation
     ------------------------------------------------------------------ */

  document.addEventListener(
    'click',
    function (e) {
      /* --- 1. Size pill? (only inside OPEN strips) ------------------ */
      var pill = e.target.closest && e.target.closest('.az-card-size-pill');
      if (!pill) {
        var openStrips = document.querySelectorAll('.az-card-size-strip.is-open');
        outer:
        for (var s = 0; s < openStrips.length; s++) {
          var pills = openStrips[s].querySelectorAll('.az-card-size-pill');
          for (var p = 0; p < pills.length; p++) {
            if (rectHit(pills[p], e)) {
              pill = pills[p];
              break outer;
            }
          }
        }
      }
      // Reject pills inside closed strips (closest() can match them
      // even though they're invisible below the card edge)
      if (pill) {
        var pillStrip = pill.closest('.az-card-size-strip');
        if (!pillStrip || !pillStrip.classList.contains('is-open')) pill = null;
      }

      if (pill) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        if (pill.disabled || pill.hasAttribute('disabled')) return;

        var strip = pill.closest('.az-card-size-strip');
        var media = strip.closest('.card__media');
        var ownerBtn = media && media.querySelector('.az-card-add-btn');
        var pillVariantId = pill.dataset.variantId;
        if (!pillVariantId) {
          console.warn('[az-card-add] No data-variant-id on pill', pill);
          return;
        }

        strip.classList.remove('is-open');
        if (ownerBtn) {
          ownerBtn.setAttribute('aria-expanded', 'false');
          addToCart(ownerBtn, pillVariantId);
        }
        return;
      }

      /* --- 2. Open strip body? (label/padding — block navigation) --- */
      var openList = document.querySelectorAll('.az-card-size-strip.is-open');
      for (var j = 0; j < openList.length; j++) {
        if (rectHit(openList[j], e)) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          return; // tap inside strip but not on a pill: do nothing
        }
      }

      /* --- 3. The + button? ----------------------------------------- */
      var btn = e.target.closest && e.target.closest('.az-card-add-btn');
      if (!btn) {
        var allBtns = document.querySelectorAll('.az-card-add-btn');
        for (var i = 0; i < allBtns.length; i++) {
          if (rectHit(allBtns[i], e)) {
            btn = allBtns[i];
            break;
          }
        }
      }

      if (!btn) {
        /* --- 4. Outside click: close any open strip, allow default -- */
        closeAllStrips(null);
        return;
      }

      // Hard-stop the click before navigation kicks in
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      // Block duplicate clicks mid-flow
      if (
        btn.classList.contains('is-loading') ||
        btn.classList.contains('is-success')
      ) {
        return;
      }

      // Flavor B: one-option product → toggle the size strip
      if (btn.dataset.hasOptions === 'true') {
        toggleStrip(btn);
        return;
      }

      // Flavor A: single-variant product → direct add (v1 behavior)
      var variantId = btn.dataset.variantId;
      if (!variantId) {
        console.warn('[az-card-add] No data-variant-id on button', btn);
        return;
      }

      addToCart(btn, variantId);
    },
    true // <-- TRUE = capture phase, runs BEFORE bubble-phase listeners
  );

  /* Esc closes any open strip */
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' || e.key === 'Esc') {
      closeAllStrips(null);
    }
  });

  /* ------------------------------------------------------------------
     Cart add + section refresh (unchanged from v1)
     ------------------------------------------------------------------ */

  function addToCart(btn, variantId) {
    btn.classList.remove('is-error');
    btn.classList.add('is-loading');

    // Sections to refresh in the response.
    // Always request cart-icon-bubble — it's a snippet rendered inside
    // <a id="cart-icon-bubble"> in Dawn's header, so there's no
    // wrapping section element to detect by ID. Shopify returns it
    // unconditionally; we handle empty-cart case in updateCartSection.
    var sectionsToRefresh = ['cart-icon-bubble'];
    if (document.querySelector('cart-drawer')) {
      sectionsToRefresh.push('cart-drawer');
    }

    var requestBody = {
      items: [
        {
          id: parseInt(variantId, 10),
          quantity: 1
        }
      ]
    };
    if (sectionsToRefresh.length > 0) {
      requestBody.sections = sectionsToRefresh.join(',');
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/javascript'
      },
      body: JSON.stringify(requestBody)
    })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok || (data.status && data.status >= 400)) {
            var msg = data.description || data.message || 'Add to cart failed';
            throw new Error(msg);
          }
          return data;
        });
      })
      .then(function (data) {
        // Update cart sections in the DOM
        if (data.sections) {
          Object.keys(data.sections).forEach(function (sectionId) {
            updateCartSection(sectionId, data.sections[sectionId]);
          });
        }

        // Dispatch event for analytics / other listeners
        document.dispatchEvent(
          new CustomEvent('cart:item-added', {
            bubbles: true,
            detail: { variantId: variantId, response: data }
          })
        );

        // Success feedback: + → ✓ for ~1.2s
        btn.classList.remove('is-loading');
        btn.classList.add('is-success');

        setTimeout(function () {
          btn.classList.remove('is-success');
        }, SUCCESS_DURATION);
      })
      .catch(function (err) {
        console.error('[az-card-add]', err.message || err);
        btn.classList.remove('is-loading');
        btn.classList.add('is-error');

        setTimeout(function () {
          btn.classList.remove('is-error');
        }, ERROR_DURATION);
      });
  }

  function updateCartSection(sectionId, sectionHTML) {
    if (!sectionHTML) return;

    var parser = new DOMParser();
    var doc = parser.parseFromString(sectionHTML, 'text/html');

    if (sectionId === 'cart-icon-bubble') {
      // Get the new bubble from the response (or null if cart is empty)
      var newBubble = doc.querySelector('.cart-count-bubble');

      // Find the cart icon link in the header — Dawn 15.x uses id="cart-icon-bubble"
      var cartIconLink =
        document.getElementById('cart-icon-bubble') ||
        document.querySelector('.header__icon--cart');

      if (cartIconLink) {
        // Remove existing bubble (if any)
        var existingBubble = cartIconLink.querySelector('.cart-count-bubble');
        if (existingBubble) existingBubble.remove();

        // Insert new bubble (if cart now has items)
        if (newBubble) {
          cartIconLink.appendChild(newBubble);
        }
      }
    } else if (sectionId === 'cart-drawer') {
      // Silently refresh cart drawer contents (without opening it)
      var cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer) return;

      // Toggle Dawn's is-empty class based on new state (without
      // overwriting other classes like 'active' that control drawer open/close)
      var newCartDrawer = doc.querySelector('cart-drawer');
      if (newCartDrawer) {
        if (newCartDrawer.classList.contains('is-empty')) {
          cartDrawer.classList.add('is-empty');
        } else {
          cartDrawer.classList.remove('is-empty');
        }
      }

      // Replace inner content
      var newInner = doc.querySelector('cart-drawer .drawer__inner');
      var existingInner = cartDrawer.querySelector('.drawer__inner');

      if (newInner && existingInner) {
        existingInner.innerHTML = newInner.innerHTML;
      }
    }
  }
})();